import { EventEmitter } from 'eventemitter3';

import { defaults, Schema, tryParse, tryStringify } from './schema';

export type PersistedApi = {
  init(): Promise<void>;
  get<K extends keyof Schema>(key: K): Schema[K];
  write<K extends keyof Schema>(key: K, value: Schema[K]): Promise<void>;
  onUpdate<K extends keyof Schema>(
    key: K,
    cb: (value: Schema[K]) => void,
  ): () => void;
  clearStorage(): Promise<void>;
};

export type { Schema } from './schema';
export { defaults } from './schema';

const LLD_STORAGE = 'LLD_STORAGE';

const broadcast = new BroadcastChannel('LLD_BROADCAST_CHANNEL');
const UPDATE_EVENT = 'LLD_UPDATE_EVENT';

let _state: Schema = defaults;
const _emitter = new EventEmitter();

export async function init() {
  broadcast.onmessage = onBroadcastMessage;
  window.onstorage = onStorage;
  const stored = readFromStorage();
  if (stored) {
    _state = stored;
  }
}
init satisfies PersistedApi['init'];

export function get<K extends keyof Schema>(key: K): Schema[K] {
  return _state[key];
}
get satisfies PersistedApi['get'];

export async function write<K extends keyof Schema>(key: K, value: Schema[K]) {
  // read latest state from possible open tabs
  const next = readFromStorage();
  if (next) {
    _state = next;
  }
  // try {
  //   if (JSON.stringify({ v: _state[key] }) === JSON.stringify({ v: value })) {
  //     console.log("writeToStorage: value didn't change, skipping write");
  //     return;
  //   }
  //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // } catch (e) {
  //   console.error('Failed to compare values', e);
  //   // Do nothing
  // }
  _state = { ..._state, [key]: value };
  writeToStorage(_state);
}
write satisfies PersistedApi['write'];

export function onUpdate<K extends keyof Schema>(
  key: K,
  cb: (value: Schema[K]) => void,
) {
  const listener = () => cb(get(key));
  _emitter.addListener('update:' + key, listener);
  return () => _emitter.removeListener('update:' + key, listener);
}
onUpdate satisfies PersistedApi['onUpdate'];

export async function clearStorage() {
  try {
    localStorage.removeItem(LLD_STORAGE);
  } catch (e) {
    console.error('Failed to clear storage', e);
  }
}
clearStorage satisfies PersistedApi['clearStorage'];

function onStorage() {
  const next = readFromStorage();
  if (next === _state) {
    return;
  }
  if (next) {
    _state = next;
    _emitter.emit('update');
  }
}

async function onBroadcastMessage({ data }: MessageEvent) {
  if (typeof data === 'object' && data.event?.type === UPDATE_EVENT) {
    const next = readFromStorage();
    if (next === _state) {
      return;
    }
    if (next) {
      _state = next;
      if (typeof data.event.key === 'string') {
        _emitter.emit('update:' + data.event.key);
      }
    } else {
      console.error(
        'persisted state: handled update from boradcast channel; but found no data',
      );
    }
  }
}

function writeToStorage(value: Schema) {
  const rawData = tryStringify(value);
  if (rawData) {
    try {
      localStorage.setItem(LLD_STORAGE, rawData);
    } catch (e) {
      console.error('Failed to write to storage', e);
    }
  }
}

let lastRawData: string | undefined;
let lastResult: Schema | undefined;
function readFromStorage(): Schema | undefined {
  let rawData: string | null = null;
  try {
    rawData = localStorage.getItem(LLD_STORAGE);
  } catch (e) {
    console.error('Failed to read from storage', e);
  }
  if (rawData) {
    if (rawData === lastRawData) {
      return lastResult;
    } else {
      const result = tryParse(rawData);
      if (result) {
        lastRawData = rawData;
        lastResult = result;
        return result;
      }
    }
  }
}
